import {
  PlaceholderConfig,
  PlaceholderContextValues
} from '@spa/lib/react/context/PlaceholderContext';
import { CmsComponentEnvelop } from '@spa/pages-converters/cms/component-envelops/CmsComponentEnvelop';

export interface HeightSinceWidthEntry {
  readonly sinceWidth: number;
  readonly height: number;
}

export const calculateComponentChildrenPlaceholder = (
  children: ReadonlyArray<CmsComponentEnvelop>,
  contextValues: PlaceholderContextValues
): PlaceholderConfig => {
  const heightSinceWidthResult: Array<HeightSinceWidthEntry> = [];

  const breakpoints: ReadonlyArray<number> = generateBreakpoints(children, contextValues);

  for (const breakpoint of breakpoints) {
    const heightSinceWidthEntries: HeightSinceWidthEntry[] = [];

    for (const child of children) {
      const calculatedPlaceholderConfig: HeightSinceWidthEntry =
        calculatePlaceholderConfigForBreakpoint(breakpoint, child, contextValues);
      heightSinceWidthEntries.push(calculatedPlaceholderConfig);
    }

    let height: number = 0;
    for (const heightSinceWidthEntry of heightSinceWidthEntries) {
      height += heightSinceWidthEntry.height;
    }

    heightSinceWidthResult.push({ sinceWidth: breakpoint, height });
  }

  return {
    heightSinceWidth: heightSinceWidthResult
  };
};

function calculatePlaceholderConfigForBreakpoint(
  breakpoint: number,
  component: CmsComponentEnvelop,
  contextValues: PlaceholderContextValues
): HeightSinceWidthEntry {
  const placeholderConfigs: ReadonlyArray<HeightSinceWidthEntry> =
    contextValues.dynamicEnvelopComponentsMap
      ?.get(component.type)
      ?.placeholderCalculator(component.component, contextValues).heightSinceWidth ?? [];

  let height: number =
    placeholderConfigs.length === 0 ? 0 : placeholderConfigs[placeholderConfigs.length - 1].height;

  for (const placeholdersConfig of placeholderConfigs) {
    if (placeholdersConfig.sinceWidth <= breakpoint) {
      height = placeholdersConfig.height;
    } else {
      break;
    }
  }

  return { sinceWidth: breakpoint, height };
}

function generateBreakpoints(
  children: ReadonlyArray<CmsComponentEnvelop>,
  contextValues: PlaceholderContextValues
): number[] {
  const breakpoints: Array<number> = [];

  for (const child of children) {
    const placeholderConfigs: ReadonlyArray<HeightSinceWidthEntry> =
      contextValues.dynamicEnvelopComponentsMap
        ?.get(child.type)
        ?.placeholderCalculator(child.component, contextValues).heightSinceWidth ?? [];
    breakpoints.push(
      ...placeholderConfigs.map((entry: HeightSinceWidthEntry): number => entry.sinceWidth)
    );
  }

  return Array.from(new Set(breakpoints)).sort((a: number, b: number): number => a - b);
}
